import '../scss/hub.scss';
import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import Head from 'next/head';
import Script from 'next/script';
import { useRouter } from 'next/router';
import CleverTapObj from 'clevertap-web-sdk/clevertap';
import { Inter, Manrope, Open_Sans, Poppins } from 'next/font/google';
import App from 'next/app';
import { useThrottledCallback } from '@react-hookz/web';

import {
  QueryClient,
  QueryClientProvider,
  Hydrate
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Auth0Provider } from '@auth0/nextjs-auth0';

import { getCookie } from 'cookies-next';
import { Analytics } from '@vercel/analytics/next';
import { SpeedInsights } from '@vercel/speed-insights/next';

import {
  SPEED_INSIGHTS_SAMPLE_RATE,
  dropAdsLandingEvent
} from '@lib/vercel-analytics';

import { useAuth0User } from '@hooks/useAuth0User';
import { CleverTap } from '@lib/clevertap';
import { CleverTapStatusProvider } from '@lib/clevertap/status-context';
import * as gtag from '@lib/gtag';
import ConditionalRenderHOC from '@components/ConditionalRenderHOC';
import ErrorBoundary from '@components/ErrorBoundary';
import Footer from '@components/footer';
import Header from '@components/header';
import { MY_TEAM_SLUGS } from '@lib/constants';
import MyTeamTabsHOC from '@components/my-team/MyTeamTabsHOC';
import AppLoginButton from '@components/AppLoginButton';
import ToastContainer from '@components/atoms/toast/ToastContainer';
import { useIsAppView } from '@hooks/useIsAppView';
import { getExternalUserId } from '@lib/utils';
import { getDefaultSEO } from '@lib/seo';
import SitewideBanner from '@components/sitewide-banner';
import { datadogRumService } from '@lib/datadogRum';

import {
  useNotificationPreferencesGuard,
  useNotificationPreferences
} from '@hooks/notifications';
import { useStoreRewardfulReferral } from '@hooks/useStoreRewardfulReferral';
import { useCaptureAttributionFromUrl } from '@hooks/useCaptureAttributionFromUrl';
import { useRecordInitiatingPage } from '@hooks/useRecordInitiatingPage';
import { useForceReplayOnPaymentRoutes } from '@hooks/useForceReplayOnPaymentRoutes';
import { useInAppAuth } from '@hooks/useInAppAuth';
import { useClevertapIdentify } from '@hooks/useClevertapIdentify';
import { useCleverTapConsentRevocation } from '@hooks/useCleverTapConsentRevocation';
import { useDemoApiRouting } from '@hooks/useDemoApiRouting';
import { useConsentOrchestrator } from '@hooks/useConsentOrchestrator';
import { useLatestSiteNavigation } from '@hooks/useLatestSiteNavigation';
import Loading from '@components/Loading';
import BottomTabNav from '@components/BottomTabNav';
import PostHogProvider from '@components/PostHogProvider';

const openSans = Open_Sans({
  subsets: ['latin'],
  variable: '--font-open-sans'
});

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['100', '200', '300', '400', '500', '600', '700', '800', '900'],
  variable: '--font-poppins'
});

// Funnel (GYTR / mini-league readiness / landing) typography. Variable fonts,
// so no weight list needed. .funnel-fonts (scss/_cms.scss) points the semantic
// --font-heading / --font-body tokens at these.
const inter = Inter({
  subsets: ['latin'],
  variable: '--font-inter'
});

const manrope = Manrope({
  subsets: ['latin'],
  variable: '--font-manrope'
});

const initializeClevertap = async (): Promise<CleverTapObj> => {
  const clevertapModule = await import('clevertap-web-sdk');

  const projectId = process.env.NEXT_PUBLIC_CLEVERTAP_PROJECT_ID;

  if (!projectId) {
    throw new Error('CleverTap project ID is not defined.');
  }

  clevertapModule.default.init(projectId);

  // FFH-2334: do NOT push `optOut` here — or anywhere. `optOut` is the only
  // privacy key that makes the SDK write to the user's CleverTap PROFILE
  // (`ct_optout`), and an opted-out profile is filtered out of every segment
  // calculation and receives no campaigns, even when it qualifies. Refusing
  // cookies must never be recorded as the user withdrawing from data
  // processing; we express it by not initialising the SDK at all (see
  // shouldInitializeCleverTap below), exactly as the Do Not Track path does.
  //
  // `useIP` is safe on its own: it only sets a local meta prop and fires no
  // request. Consent is already granted by the time we get here, so allow it.
  clevertapModule.default.privacy.push({ useIP: true });
  if (process.env.NEXT_PUBLIC_VERCEL_ENV !== 'production') {
    clevertapModule.default.setLogLevel(3);
  }

  return clevertapModule.default;
};

// FFH-2102: bounds on waiting for the SDK's readiness signal. ~2s is far longer
// than the handshake takes in practice; the point of the cap is that a change to
// the SDK's readiness probe can never leave CleverTap disabled forever.
const CLEVERTAP_READY_POLL_MS = 100;
const CLEVERTAP_READY_TIMEOUT_MS = 2000;

/**
 * Resolves once the CleverTap SDK reports itself ready, or after
 * CLEVERTAP_READY_TIMEOUT_MS regardless.
 *
 * Deliberately fails OPEN: on timeout it resolves rather than rejecting, so the
 * caller still marks the SDK ready. Failing closed would mean a readiness probe
 * that never flips (an SDK upgrade renaming `$WZRK_WR`, say) silently disables
 * identification altogether.
 *
 * Since FFH-2334 this no longer guards a consent race (there is no consent push
 * left to race) — it simply means the SDK's onload handshake has finished, so
 * `clevertapReady` reflects a genuinely usable SDK rather than a returned
 * `init()` call.
 */
const waitForCleverTapReady = async (): Promise<void> => {
  const deadline = Date.now() + CLEVERTAP_READY_TIMEOUT_MS;

  while (Date.now() < deadline) {
    if (CleverTap.isReady()) return;
    await new Promise((resolve) =>
      setTimeout(resolve, CLEVERTAP_READY_POLL_MS)
    );
  }
};

const ConsentManagerSessionListener = ({ consentManager, children }) => {
  const { user } = useAuth0User();
  const result = getExternalUserId(user);
  useEffect(() => {
    if (result) {
      consentManager.setUserId(result.toString());
    }
  }, [user, consentManager]);

  return <>{children}</>;
};

const NotificationsCheck = ({ children }) => {
  useNotificationPreferencesGuard();
  return <>{children}</>;
};

// Keep this at module scope: it wraps the whole app, and a component declared
// inside HubApp is a new type on every HubApp render, so React would remount
// the page (dropping open modals and typed input) whenever HubApp re-renders
// (FFH-3185).
const InAppAuthWrapper = ({ children }) => {
  const { isLoading } = useInAppAuth(); // only runs where legacy source == inapp

  // Only show loading spinner for in-app users
  if (isLoading) {
    return <Loading message={'Authenticating from app...'} />;
  }

  return children;
};

const HeaderFooterWrapper = ({
  children,
  isAppView,
  pathname,
  hideHeaderPaths,
  forceHide = false
}) => {
  const { hasPreferences } = useNotificationPreferences();

  const shouldHide =
    isAppView ||
    forceHide ||
    hideHeaderPaths.includes(pathname) ||
    (pathname === '/notifications' && !hasPreferences);

  return (
    <ConditionalRenderHOC shouldHide={shouldHide}>
      {children}
    </ConditionalRenderHOC>
  );
};

/**
 * Handles imperative side effects that depend on feature flags
 * Separated into its own component so it can use hooks safely
 */
const AppInitializers: React.FC<{
  dntStatus?: DntStatus;
  hasCleverTapConsent: boolean;
  consentChecked: boolean;
  clevertapReady: boolean;
  setClevertapReady: (ready: boolean) => void;
}> = ({
  dntStatus,
  hasCleverTapConsent,
  consentChecked,
  clevertapReady,
  setClevertapReady
}) => {
  const { isAppView } = useIsAppView();

  // FFH-2334: init is now gated on consent, so this effect re-runs when consent
  // flips (denied -> granted mid-session). `clevertap.init()` must only ever run
  // once, so latch on entry — before the first await, or two renders in the same
  // tick would both get past the check.
  const initStartedRef = useRef(false);

  // Point the ffh-backend client at the demo backend for demo sessions
  // before any query-consuming component fires its first request.
  useDemoApiRouting();

  useEffect(() => {
    const initCleverTap = async () => {
      // FFH-2334: consent gates INITIALISATION, not a privacy push. Denying
      // analytics means the SDK never loads — no cookies, no requests, and
      // crucially no `ct_optout` written to the user's CleverTap profile.
      // `consentChecked` matters as much as the grant itself: before the first
      // CookieYes read resolves, `hasCleverTapConsent` is the unresolved DENIED
      // default, and initialising on that would collect without consent.
      const shouldInitializeCleverTap =
        typeof window !== 'undefined' &&
        !isAppView &&
        !dntStatus?.isLoading &&
        !dntStatus?.hasRequestedDNT &&
        consentChecked &&
        hasCleverTapConsent;

      if (shouldInitializeCleverTap && !initStartedRef.current) {
        initStartedRef.current = true;
        try {
          await initializeClevertap();
          // `init()` returning is not the same as the SDK being ready — its
          // onload/session handshake is still in flight. Wait for the SDK's own
          // readiness signal so `clevertapReady` means genuinely usable.
          await waitForCleverTapReady();
          setClevertapReady(true);
        } catch (error) {
          // Failed to initialize CleverTap
          datadogRumService.addError(error, {
            context: 'clevertap-initialization'
          });
        }
      }
    };

    initCleverTap();
  }, [isAppView, dntStatus, consentChecked, hasCleverTapConsent]);

  // FFH-2334: grants need no action (init IS the grant). Only a mid-session
  // withdrawal does, and it is handled without ever writing to the profile.
  useCleverTapConsentRevocation({
    clevertapReady,
    consentChecked,
    consentGranted: hasCleverTapConsent
  });

  // Identify only when the SDK is ready AND consent is granted.
  useClevertapIdentify(clevertapReady && hasCleverTapConsent);

  return null;
};

interface DntStatus {
  hasRequestedDNT: boolean;
  isLoading: boolean;
}

const HubApp = ({ Component, pageProps }) => {
  const router = useRouter();
  const { isAppView, appVersion } = useIsAppView();
  const hideHeaderPaths = ['/error/unsupported-app-version', '/auth/error'];
  const [queryClient] = useState(() => new QueryClient());
  const siteNavigation = useLatestSiteNavigation(pageProps.siteNavigation);

  const [dntStatus, setDntStatus] = useState<DntStatus>({
    hasRequestedDNT: false,
    isLoading: true
  });

  // Lifted out of AppInitializers so the funnel tree can read SDK readiness via
  // CleverTapStatusProvider (AppInitializers renders null as a sibling).
  const [clevertapReady, setClevertapReady] = useState(false);

  // Single source of truth for CookieYes consent. Owns one ConsentManager +
  // one listener, and drives Datadog RUM. PostHog / CleverTap consume the
  // returned booleans from their own effects.
  const consent = useConsentOrchestrator(dntStatus);

  // Memoised so the context value keeps a stable reference across unrelated
  // HubApp re-renders (route changes, other state) — consumers only re-render
  // when readiness or consent actually flips.
  const cleverTapStatus = useMemo(
    () => ({ ready: clevertapReady, consentGranted: consent.clevertap }),
    [clevertapReady, consent.clevertap]
  );

  useStoreRewardfulReferral();
  // FFH-1160: capture UTMs + click IDs from window.location.search on
  // every mount + client transition. Catches direct hits that skip the
  // `go.` Cloudflare worker (affiliate links, WhatsApp / Slack URL
  // resolution, Outlook Safe Links).
  useCaptureAttributionFromUrl();

  // Tracks the page the buyer entered checkout from, so converted_page_url
  // in Stripe metadata records the funnel arm (trial vs upfront) instead of
  // /checkout-stripe on every purchase.
  useRecordInitiatingPage();

  // FFH-1932: the only place a replay is recorded at all, so a payment
  // failure comes with one.
  useForceReplayOnPaymentRoutes();

  // When mocks are requested, block page-level rendering until the MSW
  // Service Worker is fully active. Otherwise React Query fires its first
  // queryFn before the worker becomes the page's controller and the
  // initial requests bypass MSW straight to the real backend (race
  // condition that surfaces on cold loads, e.g. `cy.visit('/foo')`).
  const shouldStartMocks =
    (process.env.NODE_ENV === 'development' ||
      process.env.NEXT_PUBLIC_IS_CYPRESS === 'true') &&
    process.env.NEXT_PUBLIC_USE_MOCK_FFH_BACKEND === 'true';

  const [mocksReady, setMocksReady] = useState(!shouldStartMocks);

  useEffect(() => {
    if (!shouldStartMocks) return;

    // If the worker fails to register (e.g. wrong SW scope on a preview
    // build), still flip mocksReady so the app renders instead of staying
    // blank forever; the request will hit the real backend in that case.
    const startWorker = async () => {
      try {
        const { worker } = await import('../__mocks__/ffh-backend/browser');
        await worker.start({ onUnhandledRequest: 'bypass' });
        const { applyCypressMswOverrides } = await import(
          '../__mocks__/ffh-backend/cypress'
        );
        await applyCypressMswOverrides(worker);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to start MSW worker', error);
      } finally {
        setMocksReady(true);
      }
    };

    startWorker();
  }, [shouldStartMocks]);

  useEffect(() => {
    const dntCookie = getCookie('user-dnt');
    setDntStatus({
      isLoading: false,
      hasRequestedDNT: dntCookie == '1'
    });
  }, []);

  const handleRouteChange = useThrottledCallback(
    (url: string) => {
      gtag.pageview(url);
    },
    [gtag],
    500
  );

  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [handleRouteChange, router]);

  const shouldLoadTrackingScripts =
    !dntStatus?.isLoading &&
    !dntStatus?.hasRequestedDNT &&
    consent.hasChecked &&
    consent.advertisement;

  const shouldLoadAnalyticsScripts =
    !dntStatus?.isLoading && !dntStatus?.hasRequestedDNT;

  return (
    <>
      {/* viewport-fit=cover exposes env(safe-area-inset-*) so bottom-anchored
          UI (e.g. the paywall CTA) can clear the iOS home indicator.

          The SEO defaults sit in the same head, and are overridden by any page
          that renders its own title, description, canonical or og tags. */}
      <Head>
        <meta
          key="viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, viewport-fit=cover"
        />
        {getDefaultSEO(router.asPath)}
      </Head>
      {process.env.NEXT_PUBLIC_COOKIE_YES_ID &&
        !dntStatus?.isLoading &&
        !dntStatus?.hasRequestedDNT && (
          <Script
            src={`https://cdn-cookieyes.com/client_data/${process.env.NEXT_PUBLIC_COOKIE_YES_ID}/script.js`}
            strategy="afterInteractive"
          />
        )}
      {/* Advanced Consent Mode (Consent Mode v2): the GTM container loads
          pre-consent (DNT still respected) WITHOUT a CookieYes blocking
          attribute. The CookieYes CMP tag inside GTM sets denied-by-default
          consent on Consent Initialization, so pre-consent the Google tags
          (Google tag / GA4 / Ads / Conversion Linker) send only cookieless
          pings — enabling Google's modelled conversions — while the
          Meta/TikTok/Snap tags stay blocked by their GTM consent
          requirements until the user grants ad_storage. */}
      {shouldLoadAnalyticsScripts && !!gtag.GTM_TRACKING_ID && (
        <Script
          id="gtm-container-loader"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','${gtag.GTM_TRACKING_ID}');
        `
          }}
        />
      )}
      {shouldLoadTrackingScripts && (
        <Script
          strategy="afterInteractive"
          src={`https://www.googletagmanager.com/gtag/js?id=${gtag.GA_TRACKING_ID}`}
          /* Google Analytics uses first-party cookies - requires consent */
          data-cookieyes="cookieyes-analytics"
        />
      )}
      {shouldLoadAnalyticsScripts && (
        <Script
          id="gtag-init"
          strategy="afterInteractive"
          /* Google Analytics uses first-party cookies - requires consent */
          data-cookieyes="cookieyes-analytics"
          dangerouslySetInnerHTML={{
            __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${gtag.GA_TRACKING_ID}', {'allow_enhanced_conversions':true});
                gtag('config', '${gtag.GA_TRACKING_ID_2}');
              `
          }}
        />
      )}
      <Script
        src={`https://www.google.com/recaptcha/api.js?render=${process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_V3}`}
        /* reCAPTCHA is necessary for security and form submissions - no consent needed */
      />
      <Script
        data-collect-dnt="true"
        id="simple-analytics"
        src="https://scripts.simpleanalyticscdn.com/latest.js"
        strategy="afterInteractive"
      />
      <Script
        id="trust-pilot"
        src="https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
        strategy="afterInteractive"
        /* TrustPilot doesn't use any cookies - https://help.trustpilot.com/s/article/TrustBox-widgets-FAQ?language=en_US */
      />
      {shouldLoadTrackingScripts && (
        <Script
          id="rewardful-init"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
        (function(w,r){w._rwq=r;w[r]=w[r]||function(){(w[r].q=w[r].q||[]).push(arguments)}})(window,'rewardful');`
          }}
        />
      )}
      {shouldLoadTrackingScripts && (
        <Script async src="https://r.wdfl.co/rw.js" data-rewardful="5d6df6" />
      )}
      {/* FFH-359: the Meta Pixel (init + PageView) now fires from the GTM
          container — a single browser rail deduped with the server CAPI on a
          shared event_id. No direct fbq init/noscript here. */}
      {/* Twitter tracking initialisation */}
      {shouldLoadTrackingScripts &&
        typeof window != 'undefined' &&
        !(window as any).twq && (
          <Script
            id="twitter-tracking-initialisation"
            strategy="afterInteractive"
            /* Twitter tracking uses first-party cookies - requires consent */
            data-cookieyes="cookieyes-advertisement"
            src="/scripts/twitter.js"
            onLoad={() => {
              window.twq('config', process.env.NEXT_PUBLIC_TWITTER_TOKEN);
            }}
          />
        )}
      <div
        className={`${openSans.variable} ${poppins.variable} ${inter.variable} ${manrope.variable}`}
      >
        <div
          className={`relative overflow-clip ${
            isAppView ? 'cky-app-override' : ''
          }`}
        >
          <Auth0Provider>
            {/* The query client wraps everything that reads a hook of ours:
                useAuth0User resolves membership from ffh-backend through
                React Query (FFH-3019), so a consumer mounted above this
                would throw. */}
            <QueryClientProvider client={queryClient}>
              <InAppAuthWrapper>
                <PostHogProvider
                  hasAnalyticsConsent={consent.analytics}
                  hasConsentBeenChecked={consent.hasChecked}
                  isLeagueContextEnabled={mocksReady}
                >
                  <Hydrate state={pageProps.dehydratedState}>
                    <ConsentManagerSessionListener
                      consentManager={consent.manager}
                    >
                      <CleverTapStatusProvider value={cleverTapStatus}>
                        {/* Block the entire query-consuming tree until MSW
                            and any cypress overrides are installed. The
                            previous inner-only gate let layout-level
                            consumers (GameweekContextSync, Header,
                            MyTeamTabsHOC) fire useLeagueContext before
                            worker.use() ran, so per-test overrides for
                            /league/context never took effect (React Query
                            cached the default response first).
                            AppInitializers must also live inside: its
                            useClevertapIdentify calls useProfile, and when
                            it mounted outside the gate that GET /users/me
                            raced worker.start(), escaped MSW to the real
                            backend and poisoned the shared profile query.
                            In production mocksReady is true from the first
                            render, so nothing is delayed there. */}
                        {mocksReady && (
                          <>
                            {/* Initialize feature-dependent side effects */}
                            <AppInitializers
                              dntStatus={dntStatus}
                              hasCleverTapConsent={consent.clevertap}
                              consentChecked={consent.hasChecked}
                              clevertapReady={clevertapReady}
                              setClevertapReady={setClevertapReady}
                            />
                            <AppLoginButton
                              isAppView={isAppView}
                              appVersion={appVersion}
                            />
                            <HeaderFooterWrapper
                              isAppView={isAppView}
                              pathname={router.pathname}
                              hideHeaderPaths={hideHeaderPaths}
                              forceHide={Boolean(pageProps.hideSiteChrome)}
                            >
                              <Header siteNavigation={siteNavigation} />
                            </HeaderFooterWrapper>
                            {/* @ts-ignore */}
                            <AnimatePresence exitBeforeEnter>
                              <ErrorBoundary key={router.pathname}>
                                <SitewideBanner />
                                <MyTeamTabsHOC
                                  urlSlug={router.pathname}
                                  shouldRender={MY_TEAM_SLUGS.some((slug) => {
                                    return (
                                      router.pathname === `/my-team/${slug}`
                                    );
                                  })}
                                >
                                  <NotificationsCheck>
                                    <Component {...pageProps} />
                                  </NotificationsCheck>
                                </MyTeamTabsHOC>
                                <ToastContainer isAppView={isAppView} />
                              </ErrorBoundary>
                            </AnimatePresence>{' '}
                            <HeaderFooterWrapper
                              isAppView={isAppView}
                              pathname={router.pathname}
                              hideHeaderPaths={hideHeaderPaths}
                              forceHide={Boolean(pageProps.hideSiteChrome)}
                            >
                              <Footer />
                            </HeaderFooterWrapper>
                            <BottomTabNav
                              isAppView={isAppView}
                              appVersion={appVersion}
                            />
                          </>
                        )}
                      </CleverTapStatusProvider>
                    </ConsentManagerSessionListener>
                    <ReactQueryDevtools initialIsOpen={false} />
                  </Hydrate>
                </PostHogProvider>
              </InAppAuthWrapper>
            </QueryClientProvider>
          </Auth0Provider>
        </div>
      </div>
      {/* Vercel Web Analytics (page views/visitors) and Speed Insights (Core
          Web Vitals) replace the deprecated dashboard auto-injection. Both are
          cookieless and collect only aggregate, anonymous data, so they set no
          identifiers and need no cookie-consent or DNT gating; they render
          unconditionally by design.

          Web Analytics offers no sampling, so beforeSend is its only volume
          control. Speed Insights is left unfiltered on purpose: its script
          samples once per page load before beforeSend is consulted, so
          excluding routes there would forfeit Core Web Vitals coverage on our
          highest-traffic pages while saving almost nothing. beforeSend must
          keep a stable identity, hence the module-scope import. */}
      <Analytics beforeSend={dropAdsLandingEvent} />
      <SpeedInsights sampleRate={SPEED_INSIGHTS_SAMPLE_RATE} />
    </>
  );
};

HubApp.getInitialProps = async (appContext: any) => {
  const appProps = await App.getInitialProps(appContext);

  const req = appContext.ctx.req;
  const dntHeader = req?.headers?.dnt || req?.headers?.DNT;
  const hasRequestedDNT = dntHeader === '1'; // User explicitly requested Do Not Track

  appProps.pageProps.hasRequestedDNT = hasRequestedDNT;

  // Server only, and imported on demand: the read carries the Sanity token,
  // which must stay out of the browser bundle. In the browser the header keeps
  // the menus it already has (useLatestSiteNavigation).
  if (typeof window === 'undefined') {
    const { getSiteNavigation } = await import('@lib/sanity/siteNavigation');
    appProps.pageProps.siteNavigation = await getSiteNavigation();
  }

  return appProps;
};

export default HubApp;
